/**
 * Minimal QR Code encoder (ISO/IEC 18004) — byte mode, ECC level M,
 * versions 1–10, all eight masks with the standard penalty scoring.
 *
 * Written in-tree on purpose: the class invite code / registration link is the
 * only thing we ever encode (well under 200 bytes), and docs/UX.md §3.1 asks
 * for an inline SVG, so a whole QR library would be dead weight in the bundle.
 */

// --------------------------------------------------------------------------- //
// GF(256) — Reed-Solomon over x^8 + x^4 + x^3 + x^2 + 1
// --------------------------------------------------------------------------- //

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255]!;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}

/** Generator polynomial for `degree` error-correction codewords. */
function rsGenerator(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i += 1) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] = (next[j]! ^ gfMul(poly[j]!, EXP[i]!)) as number;
      next[j + 1] = (next[j + 1]! ^ poly[j]!) as number;
    }
    poly = next;
  }
  return poly;
}

function rsRemainder(data: Uint8Array, degree: number): Uint8Array {
  const gen = rsGenerator(degree);
  const out = new Uint8Array(degree);
  for (const byte of data) {
    const factor = byte ^ out[0]!;
    out.copyWithin(0, 1);
    out[degree - 1] = 0;
    for (let i = 0; i < degree; i += 1) {
      out[i] = (out[i]! ^ gfMul(gen[i + 1]!, factor)) as number;
    }
  }
  return out;
}

// --------------------------------------------------------------------------- //
// version tables (ECC level M, versions 1–10)
// --------------------------------------------------------------------------- //

interface VersionSpec {
  /** error-correction codewords per block */
  ec: number;
  /** [blockCount, dataCodewordsPerBlock] for group 1 and (optionally) group 2 */
  groups: Array<[number, number]>;
  /** alignment-pattern centre coordinates */
  align: number[];
}

const VERSIONS: Record<number, VersionSpec> = {
  1: { ec: 10, groups: [[1, 16]], align: [] },
  2: { ec: 16, groups: [[1, 28]], align: [6, 18] },
  3: { ec: 26, groups: [[1, 44]], align: [6, 22] },
  4: { ec: 18, groups: [[2, 32]], align: [6, 26] },
  5: { ec: 24, groups: [[2, 43]], align: [6, 30] },
  6: { ec: 16, groups: [[4, 27]], align: [6, 34] },
  7: { ec: 18, groups: [[4, 31]], align: [6, 22, 38] },
  8: {
    ec: 22,
    groups: [
      [2, 38],
      [2, 39],
    ],
    align: [6, 24, 42],
  },
  9: {
    ec: 22,
    groups: [
      [3, 36],
      [2, 37],
    ],
    align: [6, 26, 46],
  },
  10: {
    ec: 26,
    groups: [
      [4, 43],
      [1, 44],
    ],
    align: [6, 28, 50],
  },
};

const MAX_VERSION = 10;

function dataCodewords(spec: VersionSpec): number {
  return spec.groups.reduce((sum, [blocks, per]) => sum + blocks * per, 0);
}

export class QrError extends Error {}

// --------------------------------------------------------------------------- //
// bit stream
// --------------------------------------------------------------------------- //

class BitBuffer {
  readonly bits: number[] = [];

  push(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i -= 1) this.bits.push((value >>> i) & 1);
  }

  get length(): number {
    return this.bits.length;
  }

  toBytes(): Uint8Array {
    const out = new Uint8Array(Math.ceil(this.bits.length / 8));
    this.bits.forEach((bit, i) => {
      if (bit) out[i >>> 3] = (out[i >>> 3]! | (0x80 >>> (i & 7))) as number;
    });
    return out;
  }
}

/** UTF-8 bytes; QR byte mode is officially Latin-1 but every scanner reads UTF-8. */
function utf8(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

function pickVersion(byteLength: number): number {
  for (let v = 1; v <= MAX_VERSION; v += 1) {
    const spec = VERSIONS[v]!;
    const countBits = v < 10 ? 8 : 16;
    if (4 + countBits + byteLength * 8 <= dataCodewords(spec) * 8) return v;
  }
  throw new QrError(`内容太长，无法编码为二维码（${byteLength} 字节）`);
}

/** Data + EC codewords, interleaved per §8.6. */
function buildCodewords(text: string, version: number): Uint8Array {
  const spec = VERSIONS[version]!;
  const bytes = utf8(text);
  const total = dataCodewords(spec);

  const buf = new BitBuffer();
  buf.push(0b0100, 4); // byte mode
  buf.push(bytes.length, version < 10 ? 8 : 16);
  for (const b of bytes) buf.push(b, 8);
  buf.push(0, Math.min(4, total * 8 - buf.length)); // terminator
  if (buf.length % 8 !== 0) buf.push(0, 8 - (buf.length % 8));

  const data = new Uint8Array(total);
  data.set(buf.toBytes());
  for (let i = buf.toBytes().length, pad = 0; i < total; i += 1, pad += 1) {
    data[i] = pad % 2 === 0 ? 0xec : 0x11;
  }

  // split into blocks
  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;
  for (const [blocks, per] of spec.groups) {
    for (let b = 0; b < blocks; b += 1) {
      const block = data.subarray(offset, offset + per);
      offset += per;
      dataBlocks.push(block);
      ecBlocks.push(rsRemainder(block, spec.ec));
    }
  }

  // interleave
  const out: number[] = [];
  const maxData = Math.max(...dataBlocks.map((b) => b.length));
  for (let i = 0; i < maxData; i += 1) {
    for (const block of dataBlocks) if (i < block.length) out.push(block[i]!);
  }
  for (let i = 0; i < spec.ec; i += 1) {
    for (const block of ecBlocks) out.push(block[i]!);
  }
  return Uint8Array.from(out);
}

// --------------------------------------------------------------------------- //
// matrix
// --------------------------------------------------------------------------- //

type Grid = Int8Array[]; // -1 = free, 0 = light, 1 = dark

function newGrid(size: number): Grid {
  return Array.from({ length: size }, () => new Int8Array(size).fill(-1));
}

function setFn(grid: Grid, x: number, y: number, dark: boolean): void {
  grid[y]![x] = dark ? 1 : 0;
}

function placeFinder(grid: Grid, cx: number, cy: number): void {
  for (let dy = -1; dy <= 7; dy += 1) {
    for (let dx = -1; dx <= 7; dx += 1) {
      const x = cx + dx;
      const y = cy + dy;
      if (x < 0 || y < 0 || x >= grid.length || y >= grid.length) continue;
      const inner = dx >= 0 && dx <= 6 && dy >= 0 && dy <= 6;
      const ring = dx === 0 || dx === 6 || dy === 0 || dy === 6;
      const core = dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4;
      setFn(grid, x, y, inner && (ring || core));
    }
  }
}

function placeAlignment(grid: Grid, centres: number[]): void {
  const size = grid.length;
  for (const cy of centres) {
    for (const cx of centres) {
      // skip the three finder corners
      if (
        (cx === 6 && cy === 6) ||
        (cx === 6 && cy === size - 7) ||
        (cx === size - 7 && cy === 6)
      ) {
        continue;
      }
      for (let dy = -2; dy <= 2; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) {
          const ring = Math.max(Math.abs(dx), Math.abs(dy));
          setFn(grid, cx + dx, cy + dy, ring !== 1);
        }
      }
    }
  }
}

function placeStatic(grid: Grid, version: number): void {
  const size = grid.length;
  placeFinder(grid, 0, 0);
  placeFinder(grid, size - 7, 0);
  placeFinder(grid, 0, size - 7);
  placeAlignment(grid, VERSIONS[version]!.align);

  for (let i = 8; i < size - 8; i += 1) {
    setFn(grid, i, 6, i % 2 === 0);
    setFn(grid, 6, i, i % 2 === 0);
  }
  setFn(grid, 8, size - 8, true); // the always-dark module

  // reserve format areas
  for (let i = 0; i < 9; i += 1) {
    if (grid[8]![i] === -1) setFn(grid, i, 8, false);
    if (grid[i]![8] === -1) setFn(grid, 8, i, false);
  }
  for (let i = 0; i < 8; i += 1) {
    if (grid[8]![size - 1 - i] === -1) setFn(grid, size - 1 - i, 8, false);
    if (grid[size - 1 - i]![8] === -1) setFn(grid, 8, size - 1 - i, false);
  }

  if (version >= 7) {
    let bits = version;
    for (let i = 0; i < 12; i += 1) {
      bits = (bits << 1) ^ ((bits >>> 11) * 0x1f25);
    }
    const value = ((version << 12) | bits) >>> 0;
    for (let i = 0; i < 18; i += 1) {
      const dark = ((value >>> i) & 1) === 1;
      const a = Math.floor(i / 3);
      const b = (i % 3) + size - 11;
      setFn(grid, a, b, dark);
      setFn(grid, b, a, dark);
    }
  }
}

function placeData(grid: Grid, codewords: Uint8Array): void {
  const size = grid.length;
  let bit = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right -= 1; // the vertical timing column is skipped
    for (let step = 0; step < size; step += 1) {
      const y = upward ? size - 1 - step : step;
      for (const x of [right, right - 1]) {
        if (grid[y]![x] !== -1) continue;
        const byte = codewords[bit >>> 3] ?? 0;
        grid[y]![x] = ((byte >>> (7 - (bit & 7))) & 1) as number;
        bit += 1;
      }
    }
    upward = !upward;
  }
}

const MASKS: Array<(x: number, y: number) => boolean> = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

/** Format information: 5 data bits + BCH(15,5), XOR-ed with the fixed mask. */
function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask; // ECC level M = 0b00
  let rem = data;
  for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return (((data << 10) | rem) ^ 0x5412) >>> 0;
}

function placeFormat(grid: Grid, mask: number): void {
  const size = grid.length;
  const bits = formatBits(mask);
  for (let i = 0; i <= 5; i += 1) setFn(grid, 8, i, ((bits >>> i) & 1) === 1);
  setFn(grid, 8, 7, ((bits >>> 6) & 1) === 1);
  setFn(grid, 8, 8, ((bits >>> 7) & 1) === 1);
  setFn(grid, 7, 8, ((bits >>> 8) & 1) === 1);
  for (let i = 9; i < 15; i += 1) setFn(grid, 14 - i, 8, ((bits >>> i) & 1) === 1);

  for (let i = 0; i < 8; i += 1) setFn(grid, size - 1 - i, 8, ((bits >>> i) & 1) === 1);
  for (let i = 8; i < 15; i += 1) setFn(grid, 8, size - 15 + i, ((bits >>> i) & 1) === 1);
  setFn(grid, 8, size - 8, true);
}

/** §8.8.2 penalty rules 1–4; lower is better. */
function penalty(grid: Grid): number {
  const size = grid.length;
  let score = 0;

  const runScore = (run: number) => (run >= 5 ? 3 + (run - 5) : 0);
  for (let i = 0; i < size; i += 1) {
    for (const horizontal of [true, false]) {
      let run = 1;
      let prev = horizontal ? grid[i]![0]! : grid[0]![i]!;
      for (let j = 1; j < size; j += 1) {
        const cur = horizontal ? grid[i]![j]! : grid[j]![i]!;
        if (cur === prev) {
          run += 1;
        } else {
          score += runScore(run);
          run = 1;
          prev = cur;
        }
      }
      score += runScore(run);
    }
  }

  for (let y = 0; y < size - 1; y += 1) {
    for (let x = 0; x < size - 1; x += 1) {
      const v = grid[y]![x]!;
      if (v === grid[y]![x + 1] && v === grid[y + 1]![x] && v === grid[y + 1]![x + 1]) score += 3;
    }
  }

  const PATTERN = [1, 0, 1, 1, 1, 0, 1];
  const matches = (get: (k: number) => number, at: number, len: number): boolean => {
    for (let k = 0; k < 7; k += 1) if (get(at + k) !== PATTERN[k]) return false;
    const before = Array.from({ length: 4 }, (_, k) => get(at - 1 - k)).every((v) => v === 0);
    const after = Array.from({ length: 4 }, (_, k) => get(at + 7 + k)).every((v) => v === 0);
    void len;
    return before || after;
  };
  for (let i = 0; i < size; i += 1) {
    const row = (k: number) => (k < 0 || k >= size ? 0 : grid[i]![k]!);
    const col = (k: number) => (k < 0 || k >= size ? 0 : grid[k]![i]!);
    for (let j = 0; j <= size - 7; j += 1) {
      if (matches(row, j, size)) score += 40;
      if (matches(col, j, size)) score += 40;
    }
  }

  let dark = 0;
  for (const row of grid) for (const v of row) if (v === 1) dark += 1;
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;
  return score;
}

export interface QrMatrix {
  size: number;
  /** `true` = dark module. */
  modules: boolean[][];
  version: number;
}

/** Encode `text` as a QR matrix (ECC level M). Throws {@link QrError} if too long. */
export function encodeQr(text: string): QrMatrix {
  if (!text) throw new QrError("没有可编码的内容");
  const version = pickVersion(utf8(text).length);
  const codewords = buildCodewords(text, version);
  const size = version * 4 + 17;

  const base = newGrid(size);
  placeStatic(base, version);
  const reserved = base.map((row) => Int8Array.from(row, (v) => (v === -1 ? 0 : 1)));
  placeData(base, codewords);

  let best: Grid | null = null;
  let bestScore = Infinity;
  let bestMask = 0;
  for (let mask = 0; mask < 8; mask += 1) {
    const candidate = base.map((row) => Int8Array.from(row));
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        if (reserved[y]![x] === 1) continue;
        if (MASKS[mask]!(x, y)) candidate[y]![x] = (candidate[y]![x]! ^ 1) as number;
      }
    }
    placeFormat(candidate, mask);
    const score = penalty(candidate);
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
      bestMask = mask;
    }
  }
  void bestMask;

  const grid = best!;
  return {
    size,
    version,
    modules: grid.map((row) => Array.from(row, (v) => v === 1)),
  };
}

/**
 * QR as a single SVG `path` string plus its viewBox size, including the
 * mandatory 4-module quiet zone.
 */
export function qrSvgPath(
  text: string,
  quiet = 4,
): { path: string; size: number; version: number } {
  const qr = encodeQr(text);
  const parts: string[] = [];
  qr.modules.forEach((row, y) => {
    let x = 0;
    while (x < qr.size) {
      if (!row[x]) {
        x += 1;
        continue;
      }
      let run = 1;
      while (x + run < qr.size && row[x + run]) run += 1;
      parts.push(`M${x + quiet} ${y + quiet}h${run}v1h-${run}z`);
      x += run;
    }
  });
  return { path: parts.join(""), size: qr.size + quiet * 2, version: qr.version };
}
